/**
 * What the chest loot window shows (DJ 2026-10-23). A tiny module store, like the Inspect target
 * (ui/Windows/Social/inspect/inspectTarget.ts): ChestPresenter writes it from the server's
 * `chestResult` and opens the window; the window (ui/Windows/Loot/ChestLootWindow.tsx) reads it and
 * asks to take the loot. No React, no room.
 */
import { EventBus, EVENTS } from '../../../../shared/EventBus';
import { windowBridge } from '../../../ui/framework/windowBridge';

/**
 * `contents` = the loot is shown and can be taken; `taking` = Take all was pressed; `taken` = it is
 * in the bag; `bag_full`, `too_far`, `gone` (missing or empty) = could not take; `already` = this
 * character took it before.
 */
export type ChestLootStatus = 'contents' | 'taking' | 'taken' | 'bag_full' | 'too_far' | 'gone' | 'already';

export interface ChestLootView {
  chestId: string;
  name: string;
  items: ReadonlyArray<{ itemId: string; quantity: number }>;
  status: ChestLootStatus;
}

let current: ChestLootView | null = null;
/** If no answer to Take all arrives (a disconnect), the window offers it again after this long. */
export const TAKE_ANSWER_TIMEOUT_MS = 5000;
let takeTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

export function getChestLootView(): ChestLootView | null {
  return current;
}

export function subscribeChestLootView(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function setChestLootView(view: ChestLootView | null): void {
  current = view;
  for (const listener of listeners) listener();
}

/** Show a chest's loot (or why there is none) and open the window. */
export function showChestLoot(view: ChestLootView): void {
  setChestLootView(view);
  windowBridge.open('chestLoot');
}

/** Update the shown chest's status; a status for another chest is ignored. */
export function setChestLootStatus(chestId: string, status: ChestLootStatus): void {
  if (current?.chestId === chestId) setChestLootView({ ...current, status });
}

/** "Take all": ask the server to grant the shown chest's loot. */
export function takeChestLoot(bus: Pick<EventBus, 'emit'> = EventBus.getInstance()): void {
  if (!current || (current.status !== 'contents' && current.status !== 'bag_full' && current.status !== 'too_far')) return;
  const chestId = current.chestId;
  bus.emit(EVENTS.PLAYER_TAKE_CHEST_INTENT, { chestId });
  setChestLootStatus(chestId, 'taking');
  if (takeTimer) clearTimeout(takeTimer);
  takeTimer = setTimeout(() => {
    takeTimer = null;
    if (current?.chestId === chestId && current.status === 'taking') setChestLootStatus(chestId, 'contents');
  }, TAKE_ANSWER_TIMEOUT_MS);
}

export function closeChestLoot(): void {
  windowBridge.close('chestLoot');
}

/** Leaving the map: forget the shown chest and close its window (its id means nothing elsewhere). */
export function resetChestLoot(): void {
  if (takeTimer) { clearTimeout(takeTimer); takeTimer = null; }
  if (!current) return;
  setChestLootView(null);
  closeChestLoot();
}
